import { Injectable } from '@angular/core';
import { RestService } from './rest/rest.service';
import { environment } from '@environments/environment';

export type TrackingCallback = (visible: boolean) => void;

export type AnalyticsEventData = {
  type: 'view' | 'click';
  target_id: number;
  duration: number;
};

type TrackingState = {
  lastChange: number;
  visible: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private identifier: string;
  private platform = environment.isApp ? 'app' : 'web';

  private observer: IntersectionObserver;
  private tracking = new Map<Element, TrackingState>();
  private callbacks = new Map<Element, TrackingCallback>();

  constructor(private readonly restService: RestService) {
    const storageKey = 'zimmerer-analytics-service-identifier';
    const storedIdentifier = localStorage.getItem(storageKey);

    if (typeof storedIdentifier !== 'string') {
      this.identifier = crypto.randomUUID();
      localStorage.setItem(storageKey, this.identifier);
    } else {
      this.identifier = storedIdentifier;
    }

    this.observer = new IntersectionObserver((entries) => this.observerCallback(entries), {
      threshold: 0.5,
    });

    window.addEventListener('visibilitychange', () => {
      this.onVisibilityChange();
    });
  }

  /**
   * Start tracking the visibility state of the given element. The callback
   * you specify will be called whenever the visibility changes.
   */
  trackElementVisibility(element: Element, callback: TrackingCallback) {
    this.observer.observe(element);
    this.callbacks.set(element, callback);
  }

  /**
   * Stop tracking the visibility state of the given element.
   */
  stopTracking(element: Element) {
    this.observer.unobserve(element);
    this.tracking.delete(element);
    this.callbacks.delete(element);
  }

  /**
   * Send a new analytics event to the server.
   */
  addEvent(data: AnalyticsEventData): Promise<void> {
    return this.restService.post('analytics/add', {
      identifier: this.identifier,
      platform: this.platform,
      ...data,
    });
  }

  private observerCallback(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      const key = entry.target;
      const isVisible = entry.isIntersecting;

      this.updateState(key, isVisible);
    }
  }

  private onVisibilityChange() {
    const isVisible = document.visibilityState === 'visible';

    for (const key of this.tracking.keys()) {
      this.updateState(key, isVisible);
    }
  }

  private updateState(key: Element, isVisible: boolean) {
    const time = Date.now();

    if (this.tracking.has(key)) {
      const info = this.tracking.get(key);

      if (isVisible !== info.visible) {
        const cb = this.callbacks.get(key);

        if (cb) {
          cb(isVisible);
        }

        this.tracking.set(key, {
          lastChange: time,
          visible: isVisible,
        });
      }
    } else {
      this.tracking.set(key, {
        lastChange: time,
        visible: isVisible,
      });

      const cb = this.callbacks.get(key);

      if (cb) {
        cb(isVisible);
      }
    }
  }
}
